import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";

const getTreeSizeSubtitle = (title: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('5') || lowerTitle.includes('family')) {
    return '5-Pack — Trees 8ft+';
  }
  if (lowerTitle.includes('2')) {
    return '2-Pack — Trees 6-8ft';
  }
  return '1 Bottle — Trees Under 6ft';
};

export const CartDrawer = () => {
  const { 
    items, 
    isLoading, 
    updateQuantity, 
    removeItem, 
    createCheckout,
    isCartOpen,
    setCartOpen
  } = useCartStore();
  
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);

  const handleCheckout = async () => {
    try {
      // Fire Google Ads Begin Checkout conversion
      if (typeof window !== 'undefined' && (window as any).gtag) {
        const currency = items[0]?.price.currencyCode || 'USD';
        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-17766725406/lA8NCLmRj8kbEJ7u6pdC',
          'value': totalPrice,
          'currency': currency
        });
      }

      await createCheckout();
      const checkoutUrl = useCartStore.getState().checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.error('Failed to create checkout. Please try again.');
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Failed to create checkout. Please try again.');
    }
  };

  const cartButton = (
    <Button 
      variant="outline" 
      size="icon" 
      className="relative border-2 border-foreground hover:bg-foreground hover:text-background" 
      aria-label={`Shopping cart${totalItems > 0 ? `, ${totalItems} items` : ''}`}
    >
      <ShoppingCart className="h-5 w-5" />
      {totalItems > 0 && (
        <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-accent text-accent-foreground">
          {totalItems}
        </Badge>
      )}
    </Button>
  );

  return (
    <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
      {/* Desktop: HoverCard wrapper for mini preview */}
      <div className="hidden md:block">
        <HoverCard openDelay={200} closeDelay={100}>
          <HoverCardTrigger asChild>
            <SheetTrigger asChild>
              {cartButton}
            </SheetTrigger>
          </HoverCardTrigger>
          <HoverCardContent 
            align="end" 
            className="w-80 p-0"
            sideOffset={8}
          >
            {items.length === 0 ? (
              <div className="p-4 text-center">
                <ShoppingCart className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Your cart is empty</p>
              </div>
            ) : (
              <div>
                <div className="p-3 border-b border-border">
                  <p className="font-inter font-semibold text-sm">{totalItems} item{totalItems !== 1 ? 's' : ''} in cart</p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {items.slice(0, 3).map((item) => (
                    <div key={item.variantId} className="flex gap-3 p-3 border-b border-border last:border-0">
                      <div className="w-12 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={`${item.product.node.images.edges[0].node.url}&width=100`}
                            alt={item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-inter text-sm font-medium truncate">Christmas Tree Food</p>
                        <p className="text-xs text-muted-foreground">{getTreeSizeSubtitle(item.product.node.title)}</p>
                        <p className="text-sm font-semibold">${parseFloat(item.price.amount).toFixed(2)} × {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                  {items.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      +{items.length - 3} more item{items.length - 3 !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <div className="p-3 border-t border-border bg-muted/30">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-inter font-medium text-sm">Total</span>
                    <span className="font-inter font-bold">${totalPrice.toFixed(2)}</span>
                  </div>
                  <Button 
                    onClick={handleCheckout}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-inter font-semibold h-10" 
                    size="sm"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Checkout"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </HoverCardContent>
        </HoverCard>
      </div>

      {/* Mobile: Just the trigger button without hover */}
      <div className="md:hidden">
        <SheetTrigger asChild>
          {cartButton}
        </SheetTrigger>
      </div>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-cormorant text-3xl">Shopping Cart</SheetTitle>
          <SheetDescription className="font-inter">
            {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''} in your cart`}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4 p-4 border border-border rounded-xl">
                      <div className="w-20 h-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={item.product.node.images.edges[0].node.url}
                            alt={item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-cormorant text-lg font-bold">Christmas Tree Food</h4>
                        <p className="text-sm text-muted-foreground font-inter">
                          {getTreeSizeSubtitle(item.product.node.title)}
                        </p>
                        <p className="font-inter font-semibold mt-1">
                          ${parseFloat(item.price.amount).toFixed(2)}
                        </p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => removeItem(item.variantId)}
                          aria-label={`Remove ${item.product.node.title} from cart`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        
                        <div className="flex items-center gap-1 border border-border rounded-lg">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            aria-label={`Decrease quantity of ${item.product.node.title}`}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-inter font-medium" aria-label={`Quantity: ${item.quantity}`}>{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            aria-label={`Increase quantity of ${item.product.node.title}`}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex-shrink-0 space-y-4 pt-6 border-t border-border bg-background">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-cormorant font-semibold">Total</span>
                  <span className="text-2xl font-cormorant font-bold">
                    ${totalPrice.toFixed(2)}
                  </span>
                </div>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-inter font-semibold text-lg h-14" 
                  size="lg"
                  disabled={items.length === 0 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Checkout...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Checkout with Shopify
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
